import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, Shield, Clock, Award, Star, ChevronRight, Hammer, DoorOpen, Grid3X3, Bath, Home } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, projectsRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/projects`)
        ]);
        setTestimonials(testimonialsRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      title: "Windows",
      description: "Energy-efficient Low-E Argon Gas window installation and replacement for maximum comfort and savings.",
      icon: Grid3X3,
      image: "https://images.unsplash.com/photo-1631277190979-1704e8c7d574?w=800",
      features: ["Low-E Argon Gas filled", "Double-hung & Casement styles", "UV protection & noise reduction"]
    },
    {
      title: "Doors",
      description: "Custom door installation from entry doors to sliding patio doors, enhancing security and style.",
      icon: DoorOpen,
      image: "https://images.pexels.com/photos/7587880/pexels-photo-7587880.jpeg?w=800",
      features: ["Entry doors with sidelights", "Sliding patio doors", "Storm doors"]
    },
    {
      title: "Kitchens",
      description: "Complete kitchen remodeling with custom islands, white shaker cabinets, pendant lighting, and modern fixtures.",
      icon: Hammer,
      image: "https://images.unsplash.com/photo-1762529716215-afd7400d7bd4?w=800",
      features: ["White shaker cabinets", "Center islands with pendant lights", "Granite & quartz counters"]
    },
    {
      title: "Bathrooms",
      description: "Luxury bathroom renovations from walk-in showers to complete master bath remodels.",
      icon: Bath,
      image: "https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?w=800",
      features: ["Walk-in showers", "Modern vanities", "Tile work"]
    },
    {
      title: "New Construction",
      description: "Build your dream home from the ground up with our expert new construction services.",
      icon: Home,
      image: "https://images.pexels.com/photos/7601163/pexels-photo-7601163.jpeg?w=800",
      features: ["Custom home builds", "Full project management", "Quality materials"]
    }
  ];

  const stats = [
    { value: "15+", label: "Years Experience" },
    { value: "500+", label: "Projects Completed" },
    { value: "98%", label: "Customer Satisfaction" },
    { value: "24hr", label: "Response Time" }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-slate-900 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1691039923170-c275e01cce89?w=1920"
            alt="Modern home renovation"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <span className="inline-block px-4 py-2 bg-orange-500/20 text-orange-400 rounded-sm text-sm font-bold tracking-wider mb-6">
              LAGRANGE, IL'S TRUSTED CONTRACTOR
            </span>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-none mb-6 font-['Chivo']">
              Transform Your Home with
              <span className="bg-gradient-to-r from-orange-500 via-yellow-500 to-red-500 bg-clip-text text-transparent"> Expert Craftsmanship</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              From windows and doors to kitchen and bathroom renovations, plus new construction homes. 
              Quality workmanship, fair prices, and results that last.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold px-8 py-6 text-lg btn-primary"
                    data-testid="hero-quote-btn"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold font-['Chivo']">Request a Free Quote</DialogTitle>
                  </DialogHeader>
                  <QuoteForm onSuccess={() => setQuoteDialogOpen(false)} />
                </DialogContent>
              </Dialog>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-slate-600 text-white hover:bg-white/10 rounded-sm font-bold px-8 py-6 text-lg"
                asChild
              >
                <Link to="/gallery" data-testid="hero-gallery-btn">
                  View Our Work
                </Link>
              </Button>
            </div>
          </div>

          <div className="hidden lg:block relative animate-fade-in animation-delay-200">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1609946860463-34b73d0d4d8c?w=600"
                alt="Kitchen renovation"
                className="rounded-sm shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-sm shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg">Licensed & Insured</p>
                    <p className="text-slate-500">15+ Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white border-b border-slate-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            {stats.map((stat, idx) => (
              <div key={idx} className="p-8 md:p-12 text-center">
                <p className="text-4xl md:text-5xl font-black mb-2 font-['Chivo'] stat-number">
                  {stat.value}
                </p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-orange-500 font-bold tracking-wider text-sm">OUR SERVICES</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-6 font-['Chivo']">
              Expert Home Improvement Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From small updates to complete transformations, we bring expertise and 
              attention to detail to every project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <div 
                key={idx}
                className="service-card group relative overflow-hidden bg-white border border-slate-100 hover:border-orange-300 rounded-sm warm-border-hover"
                data-testid={`service-card-${service.title.toLowerCase()}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover service-image"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-sm flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 font-['Chivo']">{service.title}</h3>
                  </div>
                  <p className="text-slate-600 mb-4 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-slate-600">
                        <ChevronRight className="w-4 h-4 text-orange-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/services"
                    className="inline-flex items-center text-orange-600 font-bold hover:text-orange-700 transition-colors"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-blue-600 font-bold tracking-wider text-sm">WHY CHOOSE 875 CORP</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-6 font-['Chivo']">
                Quality You Can Trust
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                At 875 Construction Corp, we're not just contractors – we're your neighbors. 
                We take pride in every project and treat your home like it's our own.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Shield className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Licensed & Insured</h3>
                    <p className="text-slate-600">Fully licensed and insured for your peace of mind.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">On-Time Completion</h3>
                    <p className="text-slate-600">We respect your time and finish projects as promised.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Award className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Quality Guarantee</h3>
                    <p className="text-slate-600">We stand behind our work with comprehensive warranties.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/8961029/pexels-photo-8961029.jpeg?w=800"
                alt="875 Construction Corp team at work"
                className="rounded-sm shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-orange-400 font-bold tracking-wider text-sm">TESTIMONIALS</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6 font-['Chivo']">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.slice(0, 4).map((testimonial, idx) => (
              <div 
                key={idx}
                className="testimonial-card bg-slate-800 p-6 rounded-sm border border-slate-700 hover:border-orange-500/50"
                data-testid={`testimonial-card-${idx}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 leading-relaxed">"{testimonial.review}"</p>
                <div>
                  <p className="font-bold text-white">{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-r from-orange-500 via-orange-600 to-red-500" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Chivo']">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote today. Our team will work with you to 
            bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-slate-100 rounded-sm font-bold px-8 py-6 text-lg"
                  data-testid="cta-quote-btn"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </DialogTrigger>
            </Dialog>
            <Button 
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white/10 rounded-sm font-bold px-8 py-6 text-lg"
              asChild
            >
              <a href="tel:708-856-1055" data-testid="cta-call-btn">
                Call 708-856-1055
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
